/**
 * /setup?token=...
 *
 * One-time "claim this dashboard" flow (brief §6). The token in the query
 * string is validated server-side. If it's good and unused, the page lets
 * the recipient enter 2-4 admin emails + names. Submitting consumes the token
 * permanently and creates the admin rows. The /setup route then refuses to
 * load for everyone (server returns setupComplete: true).
 *
 * Calm aesthetic — this is the admin side, not the loud submitter side.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../lib/auth';

type StatusState =
  | { kind: 'checking' }
  | { kind: 'no_token' }
  | { kind: 'invalid_token' }
  | { kind: 'already_claimed' }
  | { kind: 'ready' };

type AdminInput = { email: string; name: string };

const MAX_ADMINS = 4;
const MIN_ADMINS = 2;

const blankAdmin = (): AdminInput => ({ email: '', name: '' });

export default function Setup() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [status, setStatus] = useState<StatusState>({ kind: 'checking' });
  const [adminInputs, setAdminInputs] = useState<AdminInput[]>([
    blankAdmin(),
    blankAdmin(),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ----- Status check on mount -----
  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'no_token' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/setup/status?token=${encodeURIComponent(token)}`
        );
        const data = (await res.json()) as {
          ok: boolean;
          setupComplete: boolean;
          tokenValid: boolean;
        };
        if (cancelled) return;
        if (data.setupComplete) setStatus({ kind: 'already_claimed' });
        else if (!data.tokenValid) setStatus({ kind: 'invalid_token' });
        else setStatus({ kind: 'ready' });
      } catch {
        if (!cancelled) setStatus({ kind: 'invalid_token' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ----- Form actions -----
  function updateAdmin(i: number, patch: Partial<AdminInput>) {
    setAdminInputs((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function addAdmin() {
    if (adminInputs.length >= MAX_ADMINS) return;
    setAdminInputs((prev) => [...prev, blankAdmin()]);
  }
  function removeAdmin(i: number) {
    if (adminInputs.length <= MIN_ADMINS) return;
    setAdminInputs((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');

    // Drop fully-empty rows (allows trailing-empty inputs without forcing manual remove).
    const cleaned = adminInputs
      .map((a) => ({ email: a.email.trim().toLowerCase(), name: a.name.trim() }))
      .filter((a) => a.email || a.name);

    if (cleaned.length < MIN_ADMINS) {
      setSubmitError(`Add at least ${MIN_ADMINS} admins to claim the dashboard.`);
      return;
    }
    if (cleaned.length > MAX_ADMINS) {
      setSubmitError(`That is too many. The most this allows is ${MAX_ADMINS}.`);
      return;
    }
    for (const a of cleaned) {
      if (!isEmail(a.email)) {
        setSubmitError(`"${a.email}" does not look like an email address.`);
        return;
      }
    }
    const seen = new Set<string>();
    for (const a of cleaned) {
      if (seen.has(a.email)) {
        setSubmitError(`"${a.email}" is listed twice. Use each email once.`);
        return;
      }
      seen.add(a.email);
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, admins: cleaned }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (res.ok && data.ok) {
        navigate('/login?claimed=1');
        return;
      }
      switch (data.error) {
        case 'setup_already_complete':
          setStatus({ kind: 'already_claimed' });
          break;
        case 'token_invalid':
        case 'token_used':
        case 'token_required':
          setStatus({ kind: 'invalid_token' });
          break;
        case 'admins_count_invalid':
          setSubmitError(`Add ${MIN_ADMINS} to ${MAX_ADMINS} admins.`);
          break;
        case 'admin_email_invalid':
          setSubmitError('One of those does not look like an email address.');
          break;
        case 'admin_email_duplicate':
          setSubmitError('One of those emails is listed twice.');
          break;
        default:
          setSubmitError(
            "Something went wrong on our end. Take a breath and try again in a minute."
          );
      }
    } catch {
      setSubmitError(
        "We couldn't reach the server. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      {status.kind === 'checking' && (
        <p className="text-burgundy-faint text-sm">Checking your link...</p>
      )}

      {status.kind === 'no_token' && (
        <Notice tone="bad" title="No setup link found.">
          This page is the one-time dashboard setup. It only works when opened
          through the unique link you were sent.
        </Notice>
      )}

      {status.kind === 'invalid_token' && (
        <Notice tone="bad" title="This setup link is not valid.">
          It may have been mistyped, expired, or already used. Reach out for a
          fresh link.
        </Notice>
      )}

      {status.kind === 'already_claimed' && (
        <Notice tone="ok" title="This dashboard is already claimed.">
          If you are an admin, head to{' '}
          <Link to="/login" className="text-pink-deep underline font-semibold">
            log in
          </Link>{' '}
          and request a magic link.
        </Notice>
      )}

      {status.kind === 'ready' && (
        <>
          <h1 className="font-bagel text-[40px] sm:text-[48px] text-burgundy leading-[0.95] mb-2">
            Welcome.
          </h1>
          <p className="text-burgundy-soft text-[15px] leading-relaxed mb-6 max-w-[480px]">
            This is your dilemmas dashboard. Add {MIN_ADMINS} to {MAX_ADMINS}{' '}
            admin emails below. Anyone listed here can request a magic link to
            sign in. You can add or remove admins later from Settings.
          </p>

          {submitError && (
            <div
              role="alert"
              className="mb-4 px-4 py-3 rounded-xl border border-danger bg-danger/[0.08] text-[13px] font-semibold text-danger"
            >
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {adminInputs.map((a, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row gap-2 sm:items-end"
              >
                <Field
                  label={`Email ${i + 1}`}
                  type="email"
                  value={a.email}
                  onChange={(v) => updateAdmin(i, { email: v })}
                  autoComplete="email"
                  required
                />
                <Field
                  label="Name"
                  type="text"
                  value={a.name}
                  onChange={(v) => updateAdmin(i, { name: v })}
                  autoComplete="given-name"
                  placeholder="optional"
                />
                {adminInputs.length > MIN_ADMINS && (
                  <button
                    type="button"
                    onClick={() => removeAdmin(i)}
                    className="self-start sm:self-end text-burgundy-faint hover:text-danger text-[12px] font-semibold underline px-2 py-2"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            {adminInputs.length < MAX_ADMINS && (
              <button
                type="button"
                onClick={addAdmin}
                className="text-pink-deep hover:text-burgundy text-[13px] font-semibold underline"
              >
                + Add another admin
              </button>
            )}

            <div className="pt-4 flex items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-pink text-cream-bright px-6 py-3 rounded-xl font-bold text-[14px] tracking-[0.3px] hover:bg-pink-deep disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Claiming...' : 'Claim this dashboard'}
              </button>
              <span className="text-[12px] text-burgundy-faint">
                You can only do this once.
              </span>
            </div>
          </form>
        </>
      )}
    </Shell>
  );
}

// ---------------- Sub-components ----------------

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-cream-soft text-burgundy font-hanken flex flex-col">
      <div className="h-[60px] bg-forest text-cream-bright flex items-center px-6 flex-shrink-0">
        <div className="font-rye text-[18px] tracking-[0.5px] text-cream-bright">
          <DotI>I</DotI> SAID WHAT <DotI>I</DotI> SAID
        </div>
      </div>
      <main className="flex-1 flex justify-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-[560px] bg-cream-bright border border-burgundy/[0.12] rounded-2xl p-6 sm:p-10 shadow-sm">
          {children}
        </div>
      </main>
    </div>
  );
}

function DotI({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative inline-block">
      <span
        className="absolute left-1/2 -translate-x-1/2 w-1 h-1 bg-pink rounded-full"
        style={{ top: '-6px' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="flex-1 flex flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-burgundy-faint">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="px-3 py-2.5 bg-cream border border-burgundy/[0.2] rounded-lg font-hanken text-[14px] text-burgundy placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.12)] transition-[border-color,box-shadow]"
      />
    </label>
  );
}

function Notice({
  tone,
  title,
  children,
}: {
  tone: 'ok' | 'bad';
  title: string;
  children: React.ReactNode;
}) {
  const styles =
    tone === 'ok'
      ? 'border-teal/30 bg-teal/[0.06]'
      : 'border-danger/30 bg-danger/[0.06]';
  return (
    <div className={`px-5 py-5 rounded-xl border ${styles}`}>
      <h2 className="font-bagel text-[22px] text-burgundy mb-1.5">{title}</h2>
      <p className="text-[14px] text-burgundy-soft leading-relaxed">{children}</p>
    </div>
  );
}

function isEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}
